import { useEffect, useMemo, useState } from 'react';
import { AUTH_TOKEN_KEY } from '../../../utils/authSession';
import { GoogleLogin } from '@react-oauth/google';
import { Link, useNavigate } from 'react-router-dom';
import AuthLayout from '../components/AuthLayout';
import PasswordField from '../components/PasswordField';
import { useAuth } from '../../../context/AuthContext';
import { requestPhoneOtp, verifyPhoneOtp } from '../../../services/authService';
import { authMethods } from '../../../config/authMethods';

const RegisterMode = {
  SELECT: 'select',
  PHONE: 'phone',
  EMAIL: 'email',
};

const OTP_RESEND_SECONDS = 30;

const RegisterPage = () => {
  const navigate = useNavigate();
  const { persistAuth, register } = useAuth();
  const showEmail = authMethods.emailEnabled;
  const showGoogle = authMethods.googleEnabled;
  const showPhone = authMethods.phoneEnabled;

  const [mode, setMode] = useState(() => {
    if (showEmail && showPhone) return RegisterMode.SELECT;
    if (showEmail) return RegisterMode.EMAIL;
    if (showPhone) return RegisterMode.PHONE;
    return RegisterMode.SELECT;
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [phoneStep, setPhoneStep] = useState(1);
  const [otpCooldown, setOtpCooldown] = useState(0);

  const [emailForm, setEmailForm] = useState({ name: '', email: '', password: '', confirmPassword: '' });
  const [phoneForm, setPhoneForm] = useState({ name: '', phone: '', otp: '' });

  const isAnyLoading = isSubmitting || isSendingOtp || isVerifyingOtp;

  const statusClassName = useMemo(() => {
    if (status.type === 'error') return 'alert alert-danger py-2 mb-0';
    if (status.type === 'pending') return 'alert alert-warning py-2 mb-0';
    return 'alert alert-info py-2 mb-0';
  }, [status.type]);

  useEffect(() => {
    if (otpCooldown <= 0) return undefined;
    const timer = window.setInterval(() => {
      setOtpCooldown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [otpCooldown]);

  const decodeJwt = (token) => {
    try {
      const payload = token.split('.')[1];
      const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(decoded);
    } catch (error) {
      return null;
    }
  };

  const setErrorStatus = (message) => setStatus({ type: 'error', message });
  const resetStatus = () => setStatus({ type: '', message: '' });

  const handleRegisterSuccess = (payload) => {
    if (payload.user?.approvalStatus === 'pending') {
      setStatus({ type: 'pending', message: 'Registration submitted. You are yet to be approved by the department admin.' });
      return;
    }

    const redirectPath = payload.redirectPath || '/profile';
    navigate(redirectPath);
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    const credential = credentialResponse?.credential;

    if (!credential) {
      setErrorStatus('Unable to verify your Google account.');
      return;
    }

    const profile = decodeJwt(credential);

    if (!profile?.email || !profile?.sub) {
      setErrorStatus('Unable to read Google profile details. Please try again.');
      return;
    }

    try {
      resetStatus();
      setIsSubmitting(true);
      const payload = await register({
        provider: 'google',
        name: profile.name || 'Google User',
        email: profile.email,
        googleId: profile.sub,
        googleToken: credential,
      });
      handleRegisterSuccess(payload);
    } catch (error) {
      setErrorStatus(error.message || 'Unable to create account.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEmailChange = ({ target: { name, value } }) => {
    resetStatus();
    setEmailForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhoneChange = ({ target: { name, value } }) => {
    resetStatus();
    setPhoneForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleRequestOtp = async (event) => {
    event.preventDefault();
    const name = phoneForm.name.trim();
    const phone = phoneForm.phone.trim();

    if (!name) return setErrorStatus('Full name is required for phone sign-up.');
    if (!phone) return setErrorStatus('Phone number is required.');

    try {
      resetStatus();
      setIsSendingOtp(true);
      await requestPhoneOtp({ phone, purpose: 'register', name });
      setPhoneStep(2);
      setOtpCooldown(OTP_RESEND_SECONDS);
      setStatus({ type: 'info', message: 'OTP sent. Please enter the 6-digit code to continue.' });
    } catch (error) {
      setErrorStatus(error.message || 'Unable to send OTP.');
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleVerifyAndCreatePhoneAccount = async (event) => {
    event.preventDefault();

    const name = phoneForm.name.trim();
    const phone = phoneForm.phone.trim();
    const otp = phoneForm.otp.trim();

    if (!name) return setErrorStatus('Full name is required for phone sign-up.');
    if (!phone) return setErrorStatus('Phone number is required.');
    if (!/^\d{6}$/.test(otp)) return setErrorStatus('Please enter a valid 6-digit OTP.');

    try {
      resetStatus();
      setIsVerifyingOtp(true);

      const payload = await verifyPhoneOtp({ phone, otp, purpose: 'register', name });

      // ✅ Persist token the SAME way AuthContext does
      if (payload?.token) {
        persistAuth(payload);
      }

      handleRegisterSuccess(payload);
    } catch (error) {
      setErrorStatus(error.message || 'Unable to verify OTP and create account.');
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleEmailSubmit = async (event) => {
    event.preventDefault();

    if (emailForm.password.length < 3) return setErrorStatus('Password must be at least 3 characters long.');
    if (emailForm.password !== emailForm.confirmPassword) return setErrorStatus('Passwords do not match.');

    try {
      resetStatus();
      setIsSubmitting(true);
      const payload = await register({
        provider: 'email',
        name: emailForm.name.trim(),
        email: emailForm.email.trim(),
        password: emailForm.password,
      });

      handleRegisterSuccess(payload);
    } catch (error) {
      setErrorStatus(error.message || 'Unable to create account.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleModeChange = (nextMode) => {
    resetStatus();
    setMode(nextMode);
  };

  const renderMethodSelector = () => (
    <div className="d-grid gap-2">
      {showEmail && <button type="button" className="btn btn-outline-primary" onClick={() => handleModeChange(RegisterMode.EMAIL)} disabled={isAnyLoading}>Sign up with Email</button>}
      {showPhone && <button type="button" className="btn btn-outline-primary" onClick={() => handleModeChange(RegisterMode.PHONE)} disabled={isAnyLoading}>Sign up with Mobile Number</button>}
    </div>
  );

  const renderPhoneFlow = () => (
    <form onSubmit={phoneStep === 1 ? handleRequestOtp : handleVerifyAndCreatePhoneAccount} className="d-grid gap-3">
      <div>
        <label className="form-label" htmlFor="registerPhoneName">Full Name</label>
        <input id="registerPhoneName" name="name" type="text" className="form-control" placeholder="Alex Batter" value={phoneForm.name} onChange={handlePhoneChange} required />
      </div>

      <div>
        <label className="form-label" htmlFor="registerPhoneNumber">Phone Number</label>
        <input id="registerPhoneNumber" name="phone" type="tel" className="form-control" placeholder="+1 555 123 4567" value={phoneForm.phone} onChange={handlePhoneChange} required />
      </div>

      {phoneStep === 2 && (
        <div>
          <label className="form-label" htmlFor="registerPhoneOtp">OTP (6 digits)</label>
          <input id="registerPhoneOtp" name="otp" type="text" inputMode="numeric" className="form-control" placeholder="Enter 6-digit OTP" value={phoneForm.otp} maxLength={6} onChange={handlePhoneChange} required />
        </div>
      )}

      {phoneStep === 1 ? (
        <button type="submit" className="btn btn-primary w-100" disabled={isAnyLoading}>
          {isSendingOtp ? <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />Sending OTP...</> : 'Send OTP'}
        </button>
      ) : (
        <>
          <button type="submit" className="btn btn-primary w-100" disabled={isAnyLoading}>
            {isVerifyingOtp ? <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />Verifying...</> : 'Verify & Create Account'}
          </button>
          <button type="button" className="btn btn-link p-0 text-start" onClick={(event) => handleRequestOtp(event)} disabled={isAnyLoading || otpCooldown > 0}>
            {otpCooldown > 0 ? `Resend OTP in ${otpCooldown}s` : 'Resend OTP'}
          </button>
        </>
      )}
    </form>
  );

  const renderEmailFlow = () => (
    <form onSubmit={handleEmailSubmit} className="d-grid gap-3">
      <div>
        <label className="form-label" htmlFor="registerEmailName">Full Name</label>
        <input id="registerEmailName" name="name" type="text" className="form-control" placeholder="Alex Batter" value={emailForm.name} onChange={handleEmailChange} required />
      </div>

      <div>
        <label className="form-label" htmlFor="registerEmailAddress">Email</label>
        <input id="registerEmailAddress" name="email" type="email" className="form-control" placeholder="you@example.com" value={emailForm.email} onChange={handleEmailChange} required />
      </div>

      <div>
        <label className="form-label" htmlFor="registerEmailPassword">Password</label>
        <PasswordField id="registerEmailPassword" name="password" placeholder="Choose a secure password" value={emailForm.password} onChange={handleEmailChange} required autoComplete="new-password" />
      </div>

      <div>
        <label className="form-label" htmlFor="registerEmailConfirmPassword">Confirm Password</label>
        <PasswordField id="registerEmailConfirmPassword" name="confirmPassword" placeholder="Re-enter your password" value={emailForm.confirmPassword} onChange={handleEmailChange} required autoComplete="new-password" />
      </div>

      <button type="submit" className="btn btn-primary w-100" disabled={isAnyLoading}>
        {isSubmitting ? <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />Creating account...</> : 'Create account'}
      </button>
    </form>
  );

  return (
    <AuthLayout title="Create your account" subtitle="Choose a sign-up method to continue." showLogo>
      <div className="card border-0 shadow-sm">
        <div className="card-body p-4 d-grid gap-3">
          {mode === RegisterMode.SELECT && renderMethodSelector()}
          {!showEmail && !showGoogle && !showPhone && <div className='alert alert-warning py-2 mb-0'>No login methods are currently enabled.</div>}
          {showPhone && mode === RegisterMode.PHONE && renderPhoneFlow()}
          {showEmail && mode === RegisterMode.EMAIL && renderEmailFlow()}
          {showGoogle && <GoogleLogin onSuccess={handleGoogleSuccess} onError={() => setErrorStatus('Google signup failed.')} />}

          {status.message && <div className={statusClassName} role="status">{status.message}</div>}

          <div className="text-center small">Already have an account? <Link to="/auth/login">Sign in</Link></div>
        </div>
      </div>
    </AuthLayout>
  );
};

export default RegisterPage;
